import React, { useRef } from "react";
import { FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import * as kioskApi from "components/auth/selfSignIn/services/kioskApi";

interface SignOutPageProps {
  form: FormInstance;
  welcomeMessage?: string | null;
  workflowInFields?: any;
}

const SignOutPage: React.FC<SignOutPageProps> = ({
  form,
  welcomeMessage,
  workflowInFields,
}) => {
  const { t } = useTranslation();
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Handle focus - scroll to field
  const handleFieldFocus = (e: React.FocusEvent) => {
    const target = e.target as HTMLElement;
    const formItem = target.closest(".ant-form-item");

    if (formItem) {
      setTimeout(() => {
        formItem.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  return (
    <div
      className="workflow-container"
      ref={formContainerRef}
      onFocus={handleFieldFocus}
    >
      {welcomeMessage && <div className="message">{welcomeMessage}</div>}

      <OpForm
        form={form}
        layout="vertical"
        className="signin-form"
        onSubmit={() => {}}
        hasError={false}
        defaultButtons={false}
      >
        <OpForm.Input
          name="firstName"
          label={t("selfSignIn.form.firstName")}
          placeholder={t("selfSignIn.form.firstNamePlaceholder")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("selfSignIn.form.firstNameRequired"),
            },
          ]}
        />

        {/* Middle Name - conditionally rendered based on workflowInFields */}
        {workflowInFields?.middleName?.included === 1 && (
          <OpForm.Input
            name="middleName"
            label={
              workflowInFields?.middleName?.required === 1
                ? t("selfSignIn.form.middleNameRequired")
                : t("selfSignIn.form.middleName")
            }
            placeholder={t("selfSignIn.form.middleNamePlaceholder")}
            rules={
              workflowInFields?.middleName?.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.form.middleNameRequiredMsg"),
                    },
                  ]
                : []
            }
          />
        )}

        <OpForm.Input
          name="lastName"
          label={t("selfSignIn.form.lastName")}
          placeholder={t("selfSignIn.form.lastNamePlaceholder")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("selfSignIn.form.lastNameRequiredMsg"),
            },
          ]}
        />
      </OpForm>
    </div>
  );
};

export default SignOutPage;

// API handler function to be called by parent component
export const handleSignOutNext = async (
  values: { firstName: string; lastName: string; middleName?: string },
  token: string,
  locationId: number,
): Promise<{ signedInVisitors: any[] }> => {
  try {
    // Get today's date range in YYYY-MM-DD HH:MM:SS format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const startDate = `${year}-${month}-${day} 00:00:00`;
    const endDate = `${year}-${month}-${day} 23:59:59`;

    // Call API to search for signed-in visitors - only use firstName and lastName, ignore middleName
    const apiResponse = await kioskApi.searchSignedInVisitors({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      // middleName is intentionally excluded to match visitors regardless of middle name
      token: token,
      startDate: startDate,
      endDate: endDate,
    });

    if (apiResponse && apiResponse.length > 0) {
      // Transform API response to match our visitor structure - flatten all visitors from all visits
      const visitors = apiResponse.flatMap((visit: any) => {
        // Map each visitor in the visit to a separate entry
        return (visit.visitors || []).map((visitor: any) => ({
          id: visitor.id, // Use visitorId as the primary id for selection
          visitId: visit.id,
          visitorId: visitor.id,
          visitorTypeId: visit.visitorType?.id,
          visitorType: visit.visitorType?.name,
          firstName: visitor.firstName || values.firstName.trim(),
          middleName: visitor.middleName,
          lastName: visitor.lastName || values.lastName.trim(),
          company: visit.company,
          signInTime: visitor.signIn || visit.signIn,
        }));
      });

      return { signedInVisitors: visitors };
    } else {
      return { signedInVisitors: [] };
    }
  } catch (error) {
    // On error, return empty list
    return { signedInVisitors: [] };
  }
};
